import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

// Initialize Sentry at the absolute top of the entrypoint file.
// (ES module imports always execute before other top-level code regardless
// of textual position, so this init still runs before anything else — the
// imports above were just moved to satisfy CI's import/first ESLint rule,
// which was failing the build.)
if (process.env.REACT_APP_SENTRY_DSN) {
  // NODE_ENV is baked in at build time, so a production build served on
  // localhost (e.g. scripts/e2e-gate.sh's predeploy smoke test, or plain
  // `serve -s build`) still reports NODE_ENV=production — polluting real
  // production issues in Sentry with test-sandbox noise. Localhost is never
  // real user traffic, so tag it 'test' regardless of NODE_ENV.
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const sentryEnvironment = isLocalHost ? 'test' : (process.env.NODE_ENV || 'development');

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: sentryEnvironment,
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
  });

  Sentry.setTag('appId', process.env.REACT_APP_APP_ID || 'booking-system');
  window.Sentry = Sentry; // Expose to window for legacy error handling check
}

// Ensure DOM is fully ready before mounting React
const mountApp = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Root element not found!');
    return;
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
        <App />
      </Sentry.ErrorBoundary>
    </React.StrictMode>
  );
};

// Wait for DOM to be fully loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountApp);
} else {
  // DOM is already ready
  mountApp();
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
